import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function UpliftPanel({ job, token, onRefresh }) {
  const [approving, setApproving] = useState(null);
  const uplifts = job?.uplifts || [];

  if (uplifts.length === 0 && !job?.has_pending_uplift) {
    return null;
  }

  const handleApprove = async (upliftId) => {
    setApproving(upliftId);
    try {
      const res = await axios.post(
        `${API}/api/fm/jobs/${job.id}/uplifts/${upliftId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.ok) {
        toast.success('Uplift approved');
        onRefresh?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve uplift');
    } finally {
      setApproving(null);
    }
  };

  const handleReject = async (upliftId, reason = 'Rejected by admin') => {
    const rejectReason = window.prompt('Reason for rejection:', 'Not justified');
    if (!rejectReason) return;
    
    setApproving(upliftId);
    try {
      const res = await axios.post(
        `${API}/api/fm/jobs/${job.id}/uplifts/${upliftId}/reject?reason=${encodeURIComponent(rejectReason)}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.ok) {
        toast.success('Uplift rejected');
        onRefresh?.();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject uplift');
    } finally {
      setApproving(null);
    }
  };

  const pendingUplifts = uplifts.filter(u => u.status === 'Pending Approval');
  const processedUplifts = uplifts.filter(u => u.status !== 'Pending Approval');

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="uplift-panel">
      <div className="px-4 py-3 border-b border-slate-100 bg-amber-50 flex items-center gap-2">
        <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <h3 className="font-semibold text-amber-800">Uplift Requests ({pendingUplifts.length} pending)</h3>
      </div>

      <div className="divide-y divide-slate-100">
        {pendingUplifts.map((uplift) => (
          <div key={uplift.id} className="p-4 bg-amber-50/30" data-testid={`uplift-${uplift.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    {uplift.uplift_type}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(uplift.created_at).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-slate-700 font-medium mb-1">{uplift.reason}</p>
                {uplift.supporting_notes && (
                  <p className="text-sm text-slate-500">{uplift.supporting_notes}</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Additional Cost</p>
                <p className="text-lg font-bold text-slate-800">£{uplift.additional_cost_ex_vat?.toFixed(2)}</p>
                <p className="text-xs text-slate-500">+ VAT: £{uplift.vat_amount?.toFixed(2)}</p>
                <p className="text-sm font-semibold text-teal-600">Total: £{uplift.total_inc_vat?.toFixed(2)}</p>
              </div>
            </div>
            <div className="mt-3 flex gap-2 justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleReject(uplift.id)}
                disabled={approving === uplift.id}
                data-testid={`reject-uplift-${uplift.id}`}
              >
                Reject
              </Button>
              <Button
                size="sm"
                onClick={() => handleApprove(uplift.id)}
                disabled={approving === uplift.id}
                className="bg-green-600 hover:bg-green-700"
                data-testid={`approve-uplift-${uplift.id}`}
              >
                {approving === uplift.id ? 'Processing...' : 'Approve'}
              </Button>
            </div>
          </div>
        ))}

        {processedUplifts.length > 0 && (
          <div className="p-4">
            <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Previous Uplifts</p>
            {processedUplifts.map((uplift) => (
              <div key={uplift.id} className="flex items-center justify-between py-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    uplift.status === 'Approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                  }`}>
                    {uplift.status}
                  </span>
                  <span className="text-slate-600">{uplift.uplift_type}: {uplift.reason}</span>
                </div>
                <span className="font-medium">£{uplift.total_inc_vat?.toFixed(2)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
